<template>
  <div class="settings-container">
    <div class="col-12 col-xl-6">
      <ServiceWorker ref="serviceWorker" :status="service_status" :name="service_name" :error="$v" hint="platform.settings.description.native_web_push.service_name" v-model="service_name" @setStatus="setStatus" />

      <h3 class="format-subtitle">{{ $t("platform.card.formats.nwp_request_title") }}</h3>
      <DelaySettings :class="{ 'disable-delay': checkSubscription }" :disable="checkSubscription" :delay="delay_value" v-model="delay_value" :min="0" :max="60" :variable="{ key: 's' }" :title="'platform.settings.delay'" hint="platform.settings.description.native_web_push.delay_settings" />
      <SubscriptionRequest :subscription="checkSubscription" v-model="checkSubscription" />

      <h3 class="format-subtitle">{{ $t("platform.card.formats.nwp_blackout_title") }}</h3>
      <DelaySettings :delay="blackout_range" v-model="blackout_range" :min="0" :max="100" :variable="'%'" :title="'platform.settings.blackout_range'" hint="platform.settings.description.native_web_push.blackout_settings" class="pb-2" />
      <BlackoutSettings :disabled="blackout_range === 0" :text="blackout_text" :title="blackout_title" :error="$v" @setTitle="setBlackoutTitle" @setText="setBlackoutText" />

      <h3 class="format-subtitle">{{ $t("platform.card.formats.where_not_to_show") }}</h3>
      <DisplaySettings :checkboxOnly="true" :displaySettings="displaySettings" :disableOsVersions="osVersions" />
    </div>
    <div class="col-12 col-xl-6 d-none d-xl-block">
      <ExampleScreen :disabled="service_status === 3" :title="blackout_title" :opacityValue="blackout_range" :text="blackout_text" />
    </div>
  </div>
</template>
<script>
import siteSettingsService from "@/api/siteSettingsService";
import { validationMixin } from "vuelidate";
import { required, minLength, maxLength } from "vuelidate/lib/validators";
import ServiceWorker from "@/components/PlatformSettings/ServiceWorker.vue";
import DelaySettings from "../DelaySettings.vue";
import BlackoutSettings from "../BlackoutSettings.vue";
import ExampleScreen from "../ExampleScreen.vue";
import SubscriptionRequest from "../SubscriptionRequest.vue";
import DisplaySettings from "../DisplaySettings.vue";
import { mapGetters } from "vuex";
import formatSection, { saveResult } from "@/components/PlatformCard/formatSection";

export default {
  name: "NativeWebPush",
  components: {
    DisplaySettings,
    ServiceWorker,
    DelaySettings,
    BlackoutSettings,
    ExampleScreen,
    SubscriptionRequest,
  },
  mixins: [validationMixin, formatSection],
  validations: {
    service_name: {
      required,
      minLength: minLength(3),
      maxLength: maxLength(120),
    },
    blackout_title: {
      minLength: minLength(3),
      maxLength: maxLength(120),
    },
    blackout_text: {
      minLength: minLength(3),
      maxLength: maxLength(250),
    },
  },
  data() {
    return {
      displaySettings: {
        disableDeviceSell: [],
        disableOsSell: [],
        notShowReff: false,
        disableYandex: false,
      },
      service_name: "",
      service_status: 0,
      delay_value: 0,
      blackout_title: "",
      blackout_text: "",
      blackout_range: 0,
      checkSubscription: false,
      isEnabled: null,
      osVersions: [],
    };
  },
  computed: {
    ...mapGetters({
      featureFlags: "user/getFeatureFlags",
    }),
    sectionKey() {
      return "NativeWebPush";
    },
    formState() {
      return {
        enable: true,
        id: this.$route.params.id,
        worker_name: this.service_name,
        delay: this.delay_value,
        dimming: this.blackout_range,
        caption: this.blackout_title,
        description: this.blackout_text,
        lazy_activation: this.checkSubscription,

        display_settings: {
          disable_devices: this.displaySettings.disableDeviceSell,
          disable_os: this.displaySettings.disableOsSell.map((el) => el.value) || [],
          disable_no_referer: this.displaySettings.notShowReff,
          disable_yandex_browser: this.displaySettings.disableYandex,
        },
      };
    },
  },
  methods: {
    setBlackoutTitle(val) {
      this.blackout_title = val;
    },
    setBlackoutText(val) {
      this.blackout_text = val;
    },
    setStatus(val) {
      this.service_status = val;
    },
    load() {
      const id = this.$route.params.id;
      return siteSettingsService
        .getOsVersions()
        .then((res) => {
          if (res && res.status === 200) {
            this.osVersions = res.data;
            return siteSettingsService.getNativeWebPush(id);
          }
        })
        .then((resp) => {
          if (resp && resp.data && resp.data.length !== 0) {
            this.delay_value = resp.data.delay;
            this.blackout_range = resp.data.dimming;
            this.blackout_title = resp.data.caption;
            this.blackout_text = resp.data.description;
            this.checkSubscription = resp.data.lazy_activation;

            this.displaySettings = {
              disableDeviceSell: resp.data.display_settings?.disable_devices || [],
              disableOsSell: this.osVersions.filter((el) => resp.data.display_settings?.disable_os.includes(el.value)) || [],
              notShowReff: resp.data.display_settings?.disable_no_referer || false,
              disableYandex: resp.data.display_settings?.disable_yandex_browser || false,
            };

            if (resp.data.worker_name) {
              this.service_name = resp.data.worker_name;
            }
          }
        });
    },
    validate() {
      this.$v.$touch();
      return !this.$v.$invalid;
    },
    save() {
      return siteSettingsService.setNativeWebPush(this.$route.params.id, this.formState).then((res) => saveResult(res, this.$t("save_failed")));
    },
  },
};
</script>
<style lang="scss" scoped>
.settings-container {
  display: flex;
}

.disable-delay {
  opacity: 0.6;
}

.format-subtitle {
  margin: 28px 0 14px;
  font-size: 17px;
}
</style>
